import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import io from 'socket.io-client';
import './embed.css';

// Color palette for multiple stocks
const STOCK_COLORS = [
  '#00ff88', // green
  '#00bcd4', // cyan
  '#ff6b6b', // coral
  '#ffd93d', // yellow
  '#6c5ce7', // purple
  '#a29bfe', // lavender
  '#fd79a8', // pink
  '#00cec9', // teal
];

const EmbedMultiStockChart = () => {
  const { tickers } = useParams();
  const [searchParams] = useSearchParams();

  const tickerList = tickers ? tickers.split(',').map(t => t.trim().toUpperCase()) : [];

  // URL params for customization
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#0a0a0a' : '#ffffff');
  const timeRange = searchParams.get('range') || '1h';
  const showLegend = searchParams.get('legend') !== 'false';
  const showHeader = searchParams.get('header') !== 'false';
  const normalized = searchParams.get('normalized') === 'true'; // Normalize prices to % change
  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;

  const [stocksData, setStocksData] = useState({});
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const intervalMapping = {
    '5m': '1m',
    '15m': '1m',
    '30m': '1m',
    '1h': '5m',
    '4h': '15m',
    '1d': '30m',
    '1w': '1h'
  };

  const fetchData = async () => {
    try {
      const stockPromises = tickerList.map(async (ticker) => {
        const stockResponse = await fetch(`${API_BASE_URL}/api/stocks/ticker/${ticker}`);
        if (!stockResponse.ok) return null;
        return stockResponse.json();
      });

      const stocks = await Promise.all(stockPromises);
      const validStocks = stocks.filter(s => s !== null);

      if (validStocks.length === 0) {
        throw new Error('No valid stocks found');
      }

      // Build stocks data map
      const stocksMap = {};
      validStocks.forEach(stock => {
        stocksMap[stock.ticker] = stock;
      });
      setStocksData(stocksMap);

      // Fetch history for each stock
      const interval = intervalMapping[timeRange] || '5m';
      const limit = 100;

      const historyPromises = validStocks.map(async (stock) => {
        const response = await fetch(
          `${API_BASE_URL}/api/stocks/${stock.id}/history?interval=${interval}&limit=${limit}`
        );
        if (!response.ok) return { ticker: stock.ticker, data: [] };
        const data = await response.json();
        return { ticker: stock.ticker, data: data.data || [] };
      });

      const histories = await Promise.all(historyPromises);

      // Merge all histories into unified timeline
      const timeMap = new Map();

      histories.forEach(({ ticker, data }) => {
        const basePrice = normalized && data.length > 0 ? data[0].close : 1;

        data.forEach(candle => {
          const time = new Date(candle.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          });

          if (!timeMap.has(time)) {
            timeMap.set(time, { time });
          }

          const entry = timeMap.get(time);
          if (normalized) {
            // Normalize to percentage change from first price
            entry[ticker] = ((candle.close - basePrice) / basePrice) * 100;
          } else {
            entry[ticker] = candle.close;
          }
        });
      });

      // Convert to array and sort by time
      const mergedData = Array.from(timeMap.values());
      setChartData(mergedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching multi-stock data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tickerList.length === 0) return;

    fetchData();

    // Socket for live price updates on header badges
    const socket = io(API_BASE_URL, { auth: { public: true } });
    socket.on('stock-updates', (updates) => {
      const stocksArray = Array.isArray(updates) ? updates : updates?.stocks;
      if (!stocksArray) return;
      setStocksData(prev => {
        const updated = { ...prev };
        let changed = false;
        stocksArray.forEach(u => {
          const t = u.ticker?.toUpperCase();
          if (t && updated[t]) {
            const livePrice = parseFloat(u.currentPrice || u.price);
            if (livePrice && !isNaN(livePrice)) {
              const basePrice = updated[t].basePrice || updated[t].currentPrice;
              updated[t] = {
                ...updated[t],
                currentPrice: livePrice,
                changePercent24h: basePrice > 0 ? ((livePrice - basePrice) / basePrice) * 100 : 0,
              };
              changed = true;
            }
          }
        });
        return changed ? updated : prev;
      });
    });

    const interval = setInterval(fetchData, refreshInterval);
    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [tickers, timeRange, refreshInterval, normalized]);

  if (loading) {
    return (
      <div className="embed-container" style={{ background: bg }}>
        <div className="embed-loading">Loading stocks...</div>
      </div>
    );
  }

  if (error || (Object.keys(stocksData).length === 0 && !loading)) {
    return (
      <div className="embed-container embed-multi-chart" style={{ background: bg }}>
        {showHeader && (
          <div className="embed-header multi">
            <div className="embed-title" style={{ color: '#555' }}>
              {tickerList.join(' / ') || 'Loading...'}
            </div>
          </div>
        )}
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <div className="embed-tooltip multi">
          <div className="tooltip-time">{label}</div>
          {payload.map((entry, index) => (
            <div key={index} className="tooltip-row" style={{ color: entry.color }}>
              <span className="tooltip-ticker">{entry.dataKey}:</span>
              <span className="tooltip-value">
                {normalized ? `${entry.value?.toFixed(2)}%` : formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={`embed-container embed-multi-chart ${theme}`}
      style={{ background: bg === 'transparent' ? 'transparent' : bg }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {showHeader && (
        <div className="embed-header multi">
          <div className="embed-title">
            {tickerList.join(' / ')}
          </div>
          <div className="embed-stocks-summary">
            {Object.entries(stocksData).map(([ticker, stock], index) => (
              <div key={ticker} className="stock-badge" style={{ borderColor: STOCK_COLORS[index % STOCK_COLORS.length] }}>
                <span className="badge-ticker">{ticker}</span>
                <span className={`badge-change ${stock.changePercent24h >= 0 ? 'positive' : 'negative'}`}>
                  {stock.changePercent24h >= 0 ? '+' : ''}{stock.changePercent24h?.toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="embed-chart-area" style={{ height: showHeader ? 'calc(100% - 60px)' : '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 10, fill: '#888' }}
              axisLine={{ stroke: '#333' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#888' }}
              axisLine={{ stroke: '#333' }}
              tickFormatter={v => normalized ? `${v.toFixed(0)}%` : formatCurrency(v)}
              orientation="right"
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            {showLegend && (
              <Legend
                wrapperStyle={{ fontSize: '12px', color: '#888' }}
              />
            )}
            {tickerList.map((ticker, index) => (
              <Line
                key={ticker}
                type="monotone"
                dataKey={ticker}
                stroke={STOCK_COLORS[index % STOCK_COLORS.length]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EmbedMultiStockChart;
